import React from 'react';
import { FiChevronRight, FiClipboard, FiEdit2 } from 'react-icons/fi';
import { getTeeColor, teeNeedsOutline } from '../utils/teeColors';
import './CourseStatsBar.css';

// The CR / Slope / Par / Allowance tile bar shown on a round's Setup tab. Tappable
// when editable so an admin can correct wrong course data before or in-round.
// Extracted from NewScorecardEntry.js's renderCourseStatsBar so the
// Team-Only Rounds page (TeamRoundPanel.js) can use the exact same visual
// component rather than a lookalike — one implementation, guaranteed
// identical styling on both pages.
//
// `tees`: every tee in play for this round. Course Rating, Slope and Par all
// belong to a TEE, not to the course, so as soon as a round is played off more
// than one there is no single correct set of figures to show. With one tee (or
// none named) this renders exactly the four tiles it always did; with several
// it becomes one row per tee. Allowance is a property of the DAY rather than of
// a tee, so it never repeats down the rows.
//
// `roundStarted`: whether the round is underway. Decides how loudly the
// check prompt under the figures speaks (see CourseCheckPrompt).
const CourseStatsBar = ({ course, totalPar, allowancePercent, editable, onEditClick, tees = [], roundStarted = false }) => {
  if (!course) return null;

  const parOf = (tee) => {
    const pars = tee?.hole_pars;
    if (Array.isArray(pars) && pars.length) return pars.reduce((a, p) => a + (p || 0), 0);
    return totalPar;
  };

  const commonProps = {
    onClick: editable ? () => onEditClick?.(tees[0] || null) : undefined,
    role: editable ? 'button' : undefined,
    tabIndex: editable ? 0 : undefined,
    title: editable ? 'Tap to edit course data' : undefined,
  };

  if (tees.length > 1) {
    // Each tee is its own row-shaped button. "Edit the course data" has no
    // single answer once several tees are in play, so the row you tap IS the
    // thing you edit — and allowance, being a property of the day rather than
    // of a tee, gets its own row for the same reason it sits outside the
    // columns. Falls back to a plain grid when the viewer can't edit.
    const rowInner = (tee) => (
      <>
        <span className="course-stats-multi__tee">
          <span
            className={`course-stats-multi__dot${teeNeedsOutline(tee.name) ? ' course-stats-multi__dot--outlined' : ''}`}
            style={{ backgroundColor: getTeeColor(tee.name) || '#ffffff' }}
          />
          <span className="course-stats-multi__tee-name">{tee.name}</span>
        </span>
        <span className="course-stats-multi__val">{tee.course_rating ?? 'N/A'}</span>
        <span className="course-stats-multi__val">{tee.slope_rating ?? 'N/A'}</span>
        <span className="course-stats-multi__val">{parOf(tee)}</span>
      </>
    );

    return (
      <div
        className="course-stats-multi"
        style={{ marginBottom: '16px' }}
      >
        <div className={`course-stats-multi__headrow${editable ? ' course-stats-multi__headrow--editable' : ''}`}>
          <span />
          <span className="course-stats-multi__head">CR</span>
          <span className="course-stats-multi__head">Slope</span>
          <span className="course-stats-multi__head">Par</span>
          {editable && <span className="course-stats-multi__head" />}
        </div>

        {tees.map(tee => (
          editable ? (
            <button
              key={tee.id}
              type="button"
              className="course-stats-multi__row course-stats-multi__row--editable"
              onClick={() => onEditClick?.(tee)}
              title={`Edit ${tee.name} tee data`}
            >
              {rowInner(tee)}
              <FiChevronRight className="course-stats-multi__chev" size={13} />
            </button>
          ) : (
            <div key={tee.id} className="course-stats-multi__row">
              {rowInner(tee)}
            </div>
          )
        ))}

        {editable ? (
          <button
            type="button"
            className="course-stats-multi__allowance course-stats-multi__allowance--editable"
            onClick={() => onEditClick?.(null)}
            title="Edit the handicap allowance"
          >
            <span>Allowance</span>
            <span className="course-stats-multi__allowance-right">
              <span className="course-stats-multi__allowance-val">{allowancePercent || 100}%</span>
              <FiChevronRight className="course-stats-multi__chev" size={13} />
            </span>
          </button>
        ) : (
          <div className="course-stats-multi__allowance">
            <span>Allowance</span>
            <span className="course-stats-multi__allowance-val">{allowancePercent || 100}%</span>
          </div>
        )}

        {editable && <CourseCheckPrompt roundStarted={roundStarted} multiTee />}
      </div>
    );
  }

  const tile = (label, value, color = '#1e293b') => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderLeft: label === 'CR' ? 'none' : '1px solid #e2e8f0', padding: '4px 0' }}>
      <div style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: '2px', textAlign: 'center' }}>{label}</div>
      <div style={{ fontSize: '0.9rem', fontWeight: 700, color, textAlign: 'center' }}>{value}</div>
    </div>
  );

  // One tee in play: the course's own effective figures are unambiguous, so
  // keep the denser four-tile bar rather than a one-row table.
  const only = tees[0];
  const bar = (
    <div
      className="course-stats-setup"
      {...commonProps}
      style={{
        display: 'flex',
        background: '#f8fafc',
        padding: '6px 12px',
        // Inside the editable panel the panel owns the border and the radius.
        borderRadius: editable ? 0 : '12px',
        marginBottom: editable ? 0 : '16px',
        border: editable ? 'none' : '1px solid #f1f5f9',
        alignItems: 'stretch',
        cursor: editable ? 'pointer' : 'default',
      }}
    >
      {tile('CR', only?.course_rating ?? course.course_rating ?? 'N/A')}
      {tile('Slope', only?.slope_rating ?? course.slope_rating ?? 'N/A')}
      {tile('Par', only ? parOf(only) : totalPar)}
      {tile('Allowance', `${allowancePercent || 100}%`, '#0077be')}
    </div>
  );

  if (!editable) return bar;

  // Editable: the figures and the prompt to check them are ONE card, so the
  // prompt reads as being about these numbers rather than as a banner.
  return (
    <div className="course-stats-panel">
      {bar}
      <CourseCheckPrompt
        roundStarted={roundStarted}
        onReview={() => onEditClick?.(only || null, { showHoles: true })}
      />
    </div>
  );
};

// The course data comes from public sources, and clubs re-rate tees and
// re-order stroke indexes without telling anyone — so the people who CAN fix
// it are asked to look before the first tee shot, where a correction costs
// nothing. Once the round is underway the same strip stays, quieter, because a
// fix is still possible (the modal re-scores every card) but is no longer the
// thing to do next.
//
// With several tees the rows above are the controls (the row you tap is the
// tee you edit), so the multi-tee prompt says so instead of offering a button
// that would have to guess which tee it meant.
const CourseCheckPrompt = ({ roundStarted, onReview, multiTee = false }) => {
  const title = roundStarted ? 'Course data look wrong?' : 'Check before you tee off';
  const sub = multiTee
    ? (roundStarted
      ? 'Tap a tee to fix it. Edits rescore all cards.'
      : 'Tap a tee to review its ratings, pars and SI.')
    : (roundStarted
      ? 'Edits rescore all cards.'
      : 'Sourced from public data.');

  return (
    <div className={`course-check${roundStarted ? ' course-check--quiet' : ''}${multiTee ? ' course-check--multi' : ''}`}>
      <span className="course-check__icon" aria-hidden="true"><FiClipboard size={15} /></span>
      <span className="course-check__text">
        <span className="course-check__title">{title}</span>
        <span className="course-check__sub">{sub}</span>
      </span>
      {!multiTee && (
        <button type="button" className="course-check__btn" onClick={onReview}>
          <FiEdit2 size={13} aria-hidden="true" />
          {roundStarted ? 'Edit' : 'Review'}
        </button>
      )}
    </div>
  );
};

export default CourseStatsBar;
